import Head from 'next/head';
import { MyAccountLayout } from 'src/components/templates';
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Avatar,
  useTheme,
  Textarea,
  Button,
  IconButton,
  Link,
  Divider,
} from '@chakra-ui/react';
import { ArrowBackIcon } from '@chakra-ui/icons';
import { useState, useCallback, memo, useEffect, useRef } from 'react';
import { useMessaging } from 'src/contexts/messaging';
import { Conversation, Message, MessageAttachment } from 'src/api/v1-api';
import { DEFAULT_IMAGE } from 'src/constants/config';
import { useAuth } from 'src/utils/auth';
import { CoverLoader } from 'src/components/atoms';
import { useRouter } from 'next/router';
import { useMarketplaceContext } from 'src/contexts/marketplace';
import { useSSRSafeColorModeValue } from 'src/utils/hooks/useSSRSafeColorModeValue';
import useSingleToast from 'src/utils/hooks/toast/useSingleToast';

export const getContextTypeLink = (contextType: string | null, contextId: number | null) => {
  switch (contextType) {
    case 'Listing':
      return `/listing/${contextId}`;
    case 'Order':
      return `/order/${contextId}`;
    default:
      return null;
  }
};

// Lightweight relative time for conversation list
const getRelativeTimeLabel = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMinutes / 60);
  const isToday = date.toDateString() === now.toDateString();

  if (diffMinutes < 1) return 'Just now';
  if (isToday) {
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';

  // Within last 7 days, show weekday
  if (diffHours < 24 * 7) {
    return date.toLocaleDateString([], { weekday: 'short' });
  }
  // Fallback to short date
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const getDaySeparatorLabel = (iso: string): string => {
  const date = new Date(iso);
  const now = new Date();
  if (date.toDateString() === now.toDateString()) return 'Today';
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
};

const formatTimeOnly = (iso: string): string =>
  new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

const ConversationList = memo(
  ({
    conversations,
    selectedConversation,
    currentAccountId,
    borderColor,
    textColor,
    mutedTextColor,
    onConversationSelect,
    getMessagePreview,
    notificationsCount,
  }: {
    conversations: Conversation[] | null;
    selectedConversation: Conversation | null;
    currentAccountId: number;
    borderColor: string;
    textColor: string;
    mutedTextColor: string;
    onConversationSelect: (conversation: Conversation) => void;
    getMessagePreview: (conversation: Conversation) => string;
    notificationsCount: number;
  }) => {
    const theme = useTheme();
    const cardBg = useSSRSafeColorModeValue('white', 'gray.800');
    return (
      <VStack
        spacing={0}
        align="stretch"
        h="full"
        w={{ base: 'full', md: '320px', lg: '360px' }}
        bg="transparent"
      >
        {/* Conversations Sidebar Header */}
        <Box
          p={4}
          borderBottom="1px solid"
          borderColor={borderColor}
          position="sticky"
          top={0}
          zIndex={1}
          bg={cardBg}
        >
          <HStack justify="space-between" align="center">
            <Text fontWeight="semibold" fontSize="md" color={textColor}>
              Conversations
            </Text>
            {notificationsCount > 0 && (
              <Box
                bgColor={theme.colors.primary[500]}
                color="white"
                borderRadius="full"
                px={2}
                py={1}
                minW="20px"
                textAlign="center"
              >
                <Text fontSize="xs" fontWeight="bold">
                  {notificationsCount} unread
                </Text>
              </Box>
            )}
          </HStack>
        </Box>

        {/* Conversations List */}
        <VStack spacing={0} align="stretch" flex={1} overflowY="auto">
          {conversations !== null && conversations.length === 0 ? (
            <Box p={8} textAlign="center">
              <Text color={mutedTextColor}>No conversations yet</Text>
            </Box>
          ) : conversations === null ? (
            <CoverLoader context="MessagesPage:LoadingConversations" />
          ) : (
            conversations.map((conversation, index) => {
              const isSelected = selectedConversation?.id === conversation.id;
              const hasUnreadMessages =
                conversation.participants.find((p) => p.marketplace_account_id === currentAccountId)
                  ?.most_recent_message_id !== conversation.last_message_id;
              // Get all participants except the current user
              const otherParticipants = conversation.participants.filter(
                (p) => p.marketplace_account_id !== currentAccountId,
              );
              const displayName =
                conversation.title ||
                (otherParticipants.length === 1
                  ? formatParticipantName(otherParticipants[0])
                  : otherParticipants.length > 1
                  ? 'Group Chat'
                  : 'Unknown User');
              const displayAvatar = otherParticipants.length === 1 ? otherParticipants[0] : null;

              return (
                <Box
                  key={conversation.id}
                  data-test={`conversation-${index}`}
                  p={4}
                  cursor="pointer"
                  borderBottom="1px solid"
                  borderColor={borderColor}
                  onClick={() => onConversationSelect(conversation)}
                  transition="background-color 0.2s"
                  bg={isSelected ? `${theme.colors.primary[50]}` : 'transparent'}
                  _hover={{ bg: isSelected ? `${theme.colors.primary[100]}` : 'gray.50' }}
                  borderLeft={
                    isSelected ? `4px solid ${theme.colors.primary[500]}` : '4px solid transparent'
                  }
                >
                  <HStack spacing={3} align="start">
                    <Avatar
                      size="md"
                      name={displayName}
                      src={
                        displayAvatar?.image_url && displayAvatar.image_url !== DEFAULT_IMAGE
                          ? displayAvatar.image_url
                          : undefined
                      }
                    />
                    <VStack spacing={1} align="start" flex={1} minW={0}>
                      <HStack justify="space-between" w="full">
                        <Text fontWeight="semibold" fontSize="sm" color={textColor} isTruncated>
                          {displayName}
                        </Text>
                        {conversation.last_message_at && (
                          <Text fontSize="xs" color={mutedTextColor} whiteSpace="nowrap">
                            {getRelativeTimeLabel(conversation.last_message_at)}
                          </Text>
                        )}
                      </HStack>
                      <HStack spacing={2} w="full" align="center">
                        {hasUnreadMessages && (
                          <Box
                            w="10px"
                            h="10px"
                            bgColor={theme.colors.primary[500]}
                            borderRadius="full"
                            flexShrink={0}
                            boxShadow="0 0 0 2px rgba(255, 255, 255, 0.1)"
                            _dark={{
                              boxShadow: '0 0 0 2px rgba(0, 0, 0, 0.1)',
                            }}
                          />
                        )}
                        <Text
                          fontSize="xs"
                          color={hasUnreadMessages ? textColor : mutedTextColor}
                          fontWeight={hasUnreadMessages ? 'medium' : 'normal'}
                          isTruncated
                          flex={1}
                        >
                          {getMessagePreview(conversation)}
                        </Text>
                      </HStack>
                    </VStack>
                  </HStack>
                </Box>
              );
            })
          )}
        </VStack>
      </VStack>
    );
  },
);
ConversationList.displayName = 'ConversationList';

const formatParticipantName = (p: Conversation['participants'][number]): string => {
  const first = (p as any).first_name as string | undefined;
  const last = (p as any).last_name as string | undefined;
  if (first && last) {
    return `${first} ${last.charAt(0).toUpperCase()}.`;
  }
  if (first) return first;
  return p.handle || 'Unknown';
};

const getSenderName = (senderAccountId: number, conversation: Conversation | null): string => {
  if (!conversation) return 'Unknown';
  const participant = conversation.participants.find(
    (p) => p.marketplace_account_id === senderAccountId,
  );
  return participant ? formatParticipantName(participant) : 'Unknown';
};

const MessageDisplay = memo(
  ({
    selectedConversation,
    messages,
    currentAccountId,
    borderColor,
    textColor,
    mutedTextColor,
    onBackToList,
    messagingLoading,
    hasConversations,
  }: {
    selectedConversation: Conversation | null;
    messages: Message[];
    currentAccountId: number;
    borderColor: string;
    textColor: string;
    mutedTextColor: string;
    onBackToList: () => void;
    messagingLoading: boolean;
    hasConversations: boolean;
  }) => {
    const theme = useTheme();
    const showToast = useSingleToast();
    const [inputMessage, setInputMessage] = useState('');
    const [attachments, setAttachments] = useState<MessageAttachment[]>([]);
    const [isSending, setIsSending] = useState(false);
    const { addMessageToConversation } = useMessaging();
    const messagesBgColor = useSSRSafeColorModeValue('white', 'gray.900');
    const cardBg = useSSRSafeColorModeValue('white', 'gray.800');
    const messagesContainerRef = useRef<HTMLDivElement | null>(null);

    const scrollToBottom = (smooth = false) => {
      const el = messagesContainerRef.current;
      if (!el) return;
      try {
        el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
      } catch {
        el.scrollTop = el.scrollHeight;
      }
    };

    const handleInputMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const el = e.target;
      // Auto-grow
      el.style.height = 'auto';
      const nextHeight = Math.min(el.scrollHeight, 160);
      el.style.height = `${nextHeight}px`;
      setInputMessage(el.value);
    };

    const handleSendMessage = async () => {
      if (!selectedConversation) return;
      if (inputMessage.length === 0) return;
      try {
        setIsSending(true);
        await addMessageToConversation(selectedConversation.id, inputMessage);
        setInputMessage('');
        setAttachments([]);
        scrollToBottom(true);
      } catch (error) {
        showToast({
          title: 'Failed to send message',
          description: 'Your message could not be sent. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsSending(false);
      }
    };
    // Scroll to bottom when conversation changes or new messages arrive
    useEffect(() => {
      scrollToBottom();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedConversation?.id]);

    useEffect(() => {
      scrollToBottom(true);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages.length]);

    const contextTypeLink = selectedConversation
      ? getContextTypeLink(
          selectedConversation.subject_content_type_name,
          selectedConversation.subject_object_id,
        )
      : null;

    return (
      <Flex direction="column" h="full" flex={1} bg="transparent">
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <Box
              p={4}
              borderBottom="1px solid"
              borderColor={borderColor}
              bg={cardBg}
              position="sticky"
              top={0}
              zIndex={1}
            >
              <HStack spacing={3} justify="space-between" align="center">
                <HStack spacing={3}>
                  <IconButton
                    aria-label="Back to conversations"
                    icon={<ArrowBackIcon />}
                    variant="ghost"
                    display={{ base: 'flex', md: 'none' }}
                    onClick={onBackToList}
                  />
                  <Avatar
                    size="sm"
                    name={selectedConversation.title || 'Group Chat'}
                    src={(() => {
                      const imageUrl = selectedConversation.participants.find(
                        (p) => p.marketplace_account_id !== currentAccountId,
                      )?.image_url;
                      return imageUrl && imageUrl !== DEFAULT_IMAGE ? imageUrl : undefined;
                    })()}
                  />
                  <VStack spacing={0} align="start">
                    <HStack>
                      {contextTypeLink ? (
                        <Link href={contextTypeLink} textDecoration="underline">
                          <Text as="span" fontWeight="semibold" fontSize="md" color={textColor}>
                            {selectedConversation.title || 'Group Chat'}
                          </Text>
                        </Link>
                      ) : (
                        <Text fontWeight="semibold" fontSize="md" color={textColor}>
                          {selectedConversation.title || 'Group Chat'}
                        </Text>
                      )}
                    </HStack>
                  </VStack>
                </HStack>
                {/* TODO: Add thread actions */}
                {/* <Menu>
                  <MenuButton
                    as={IconButton}
                    aria-label="Thread actions"
                    icon={<ChevronDownIcon />}
                    variant="ghost"
                  />
                  <MenuList>
                    <MenuItem>Mute</MenuItem>
                    <MenuItem>Leave conversation</MenuItem>
                    <MenuItem>Report</MenuItem>
                  </MenuList>
                </Menu> */}
              </HStack>
            </Box>

            {messagingLoading ? (
              <CoverLoader context="MessagesPage:LoadingMessages" />
            ) : (
              <>
                {/* Messages Area */}
                <VStack
                  ref={messagesContainerRef}
                  spacing={4}
                  align="stretch"
                  flex={1}
                  p={4}
                  overflowY="auto"
                  overflowX="hidden"
                  bg={messagesBgColor}
                  aria-live="polite"
                >
                  {messages.length === 0 ? (
                    <Box textAlign="center" py={8}>
                      <Text color={mutedTextColor}>Start the conversation!</Text>
                    </Box>
                  ) : (
                    (() => {
                      const ordered = [...messages].sort(
                        (a, b) =>
                          new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
                      );
                      const nodes: React.ReactNode[] = [];
                      let lastDay: string | null = null;
                      let currentGroup: { senderId: number; items: Message[] } | null = null;

                      const flushGroup = () => {
                        if (!currentGroup) return;
                        const isOwn = currentGroup.senderId === currentAccountId;
                        const groupItems = currentGroup.items;
                        const last = groupItems[groupItems.length - 1];
                        const senderName = getSenderName(
                          currentGroup.senderId,
                          selectedConversation,
                        );
                        nodes.push(
                          <VStack key={`group-${last.id}`} align={isOwn ? 'end' : 'start'} minW={0}>
                            <VStack spacing={1} align={isOwn ? 'end' : 'start'} maxW="65%" minW={0}>
                              {groupItems.map((m) => (
                                <Box
                                  key={m.id}
                                  bg={isOwn ? theme.colors.primary[500] : cardBg}
                                  color={isOwn ? 'white' : textColor}
                                  px={3}
                                  py={2}
                                  borderRadius="xl"
                                  borderBottomRightRadius={isOwn ? 'sm' : 'xl'}
                                  borderBottomLeftRadius={isOwn ? 'xl' : 'sm'}
                                  border={isOwn ? 'none' : '1px solid'}
                                  borderColor={borderColor}
                                  boxShadow={isOwn ? 'none' : 'sm'}
                                  maxW="100%"
                                >
                                  <Text
                                    fontSize="sm"
                                    whiteSpace="pre-wrap"
                                    wordBreak="break-word"
                                    sx={{ overflowWrap: 'anywhere' }}
                                  >
                                    {m.body}
                                  </Text>
                                </Box>
                              ))}
                            </VStack>
                            <HStack spacing={2} opacity={0.7}>
                              {!isOwn && (
                                <Text fontSize="xs" color={mutedTextColor}>
                                  {senderName}
                                </Text>
                              )}
                              <Text fontSize="xs" color={mutedTextColor}>
                                {formatTimeOnly(last.created_at)}
                              </Text>
                            </HStack>
                          </VStack>,
                        );
                        currentGroup = null;
                      };

                      ordered.forEach((m) => {
                        const day = new Date(m.created_at).toDateString();
                        if (day !== lastDay) {
                          // flush previous
                          flushGroup();
                          lastDay = day;
                          nodes.push(
                            <HStack
                              key={`day-${m.id}`}
                              justify="center"
                              my={2}
                              w="full"
                              align="center"
                            >
                              <Divider />
                              <Text fontSize="xs" color={mutedTextColor} whiteSpace="nowrap" px={2}>
                                {getDaySeparatorLabel(m.created_at)}
                              </Text>
                              <Divider />
                            </HStack>,
                          );
                        }

                        if (!currentGroup || currentGroup.senderId !== m.sender_id) {
                          flushGroup();
                          currentGroup = { senderId: m.sender_id, items: [m] };
                        } else {
                          currentGroup.items.push(m);
                        }
                      });
                      flushGroup();
                      return nodes;
                    })()
                  )}
                </VStack>

                {/* Message Input */}
                <Box
                  p={4}
                  borderTop="1px solid"
                  borderColor={borderColor}
                  bg={cardBg}
                  position="sticky"
                  bottom={0}
                  zIndex={1}
                >
                  <HStack spacing={2} align="end">
                    {/* TODO: Add attachment support */}
                    {/* <Tooltip label="Attach files">
                      <IconButton aria-label="Attach" icon={<AttachmentIcon />} variant="ghost" />
                    </Tooltip> */}
                    <Textarea
                      placeholder="Type a message..."
                      borderRadius="md"
                      flex={1}
                      minH="40px"
                      maxH="160px"
                      resize="none"
                      rows={1}
                      border="1px solid"
                      borderColor={borderColor}
                      _focus={{
                        borderColor: theme.colors.primary[500],
                        boxShadow: `0 0 0 1px ${theme.colors.primary[500]}`,
                      }}
                      value={inputMessage}
                      onChange={handleInputMessageChange}
                      onKeyDown={(e) => {
                        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                          e.preventDefault();
                          if (!isSending) {
                            handleSendMessage();
                          }
                        }
                      }}
                    />
                    <Button
                      colorScheme="primary"
                      borderRadius="full"
                      px={6}
                      onClick={handleSendMessage}
                      isDisabled={inputMessage.length === 0 || isSending}
                      isLoading={isSending}
                    >
                      Send
                    </Button>
                  </HStack>
                </Box>
              </>
            )}
          </>
        ) : (
          <Flex direction="column" align="center" justify="center" flex={1} w="full">
            <Box textAlign="center" w="full" px={4}>
              {hasConversations ? (
                <>
                  <Text fontSize="lg" color={mutedTextColor} mb={2}>
                    Select a conversation to start messaging
                  </Text>
                </>
              ) : (
                <Text fontSize="lg" color={mutedTextColor} mb={2}>
                  No conversations yet
                </Text>
              )}
            </Box>
          </Flex>
        )}
      </Flex>
    );
  },
);
MessageDisplay.displayName = 'MessageDisplay';

const Messages = () => {
  const {
    conversations,
    conversationsMessages,
    notificationsCount,
    fetchConversationMessages,
    updateConversationReadStatus,
    messagingLoading,
  } = useMessaging();
  const router = useRouter();
  const { user, userLoaded, isAuthenticated } = useAuth();
  const { marketplace } = useMarketplaceContext();
  const { conversationId } = router.query;
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [isMobileView, setIsMobileView] = useState(false);
  const [availableHeight, setAvailableHeight] = useState('600px');
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    // If we are creating a new conversation, preload the new conversation
    // so the user can immediately post the first message
    if (conversationId && conversations !== null && selectedConversation === null) {
      const queryConvoId = parseInt(conversationId as string);
      const foundConvo = conversations.find((c) => c.id === queryConvoId);
      if (foundConvo) {
        setSelectedConversation(foundConvo);
        fetchConversationMessages(queryConvoId, false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, conversations, selectedConversation]);

  // TODO: This is a barebones approach that should be improved by using server sent events or a websocket.
  // Auto-fetch conversation messages every 30 seconds when a conversation is selected
  useEffect(() => {
    if (!selectedConversation || !isAuthenticated) {
      return;
    }

    let isFetching = false;
    const intervalId = setInterval(async () => {
      // Guard against overlapping calls if the previous fetch is still in progress
      // Also check if user is still authenticated
      if (!isFetching && isAuthenticated) {
        isFetching = true;
        try {
          await fetchConversationMessages(selectedConversation.id, false); // Don't show loading for auto-refresh
        } finally {
          isFetching = false;
        }
      }
    }, 30000); // 30 seconds

    return () => {
      clearInterval(intervalId);
    };
  }, [selectedConversation, fetchConversationMessages, isAuthenticated]);

  const borderColor = useSSRSafeColorModeValue('gray.200', 'gray.600');
  const textColor = useSSRSafeColorModeValue('gray.900', 'white');
  const mutedTextColor = useSSRSafeColorModeValue('gray.600', 'gray.400');
  const pageBg = useSSRSafeColorModeValue('gray.50', 'gray.900');

  // get the user's marketplace account id for the current marketplace
  //const currentAccountId = 1;
  const currentAccountId =
    (user &&
      marketplace &&
      user.marketplace_accounts.find((account) => account.marketplace_id === marketplace.id)?.id) ||
    0;

  const getMessagePreview = useCallback((conversation: Conversation): string => {
    if (!conversation.last_message_text) return 'No messages yet';

    return conversation.last_message_text.length > 50
      ? conversation.last_message_text.substring(0, 50) + '...'
      : conversation.last_message_text;
  }, []);

  const handleConversationSelect = useCallback(
    async (conversation: Conversation) => {
      setSelectedConversation(conversation);
      await fetchConversationMessages(conversation.id, false);
      setIsMobileView(true);

      // Check if this conversation has unread messages for the current user
      const hasUnreadMessages =
        conversation.participants.find((p) => p.marketplace_account_id === currentAccountId)
          ?.most_recent_message_id !== conversation.last_message_id;

      // Update read status if there are unread messages
      if (hasUnreadMessages) {
        updateConversationReadStatus(conversation.id);
      }
    },
    [currentAccountId, updateConversationReadStatus],
  );

  const handleBackToList = useCallback(() => {
    setIsMobileView(false);
    setSelectedConversation(null);
  }, []);

  // Calculate available height based on viewport and header
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const calculateHeight = () => {
      // Use visualViewport if available (better for mobile), fallback to window.innerHeight
      const viewportHeight = (window as any).visualViewport?.height || window.innerHeight;

      // Detect if we're on mobile
      const isMobile = window.innerWidth < 768;

      // Account for header, navigation, and padding
      const reservedHeight = 210;
      const calculatedHeight = Math.max(isMobile ? 300 : 400, viewportHeight - reservedHeight);
      setAvailableHeight(`${calculatedHeight}px`);
    };

    const debouncedCalculateHeight = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(calculateHeight, 100); // Debounce by 100ms
    };

    // Calculate on mount
    calculateHeight();

    // Recalculate on window resize and visual viewport change (for mobile browser UI changes)
    window.addEventListener('resize', debouncedCalculateHeight);
    if ((window as any).visualViewport) {
      (window as any).visualViewport.addEventListener('resize', debouncedCalculateHeight);
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', debouncedCalculateHeight);
      if ((window as any).visualViewport) {
        (window as any).visualViewport.removeEventListener('resize', debouncedCalculateHeight);
      }
    };
  }, []);

  return (
    <>
      <Head>
        <title>Messages</title>
        <meta name="description" content="Messages" />
      </Head>
      <MyAccountLayout title="Messages" currentPath="/account/messages">
        {!isMounted ? (
          <CoverLoader context="MessagesPage:InitializingPage" />
        ) : userLoaded && !isAuthenticated ? (
          <Text fontSize="md" color="gray.600" lineHeight="1.6" textAlign="center" m={10}>
            This feature is not currently available.
          </Text>
        ) : (
          <Box w="full" h={availableHeight} overflow="hidden" bg={pageBg} p={0}>
            <Flex h="full" gap={0}>
              {/* Mobile/Tablet responsive logic */}
              <Box
                display={{ base: isMobileView ? 'none' : 'block', md: 'block' }}
                w={{ base: 'full', md: 'auto' }}
                pr={{ base: 0, md: 0 }}
              >
                <ConversationList
                  conversations={conversations}
                  selectedConversation={selectedConversation}
                  currentAccountId={currentAccountId}
                  borderColor={borderColor}
                  textColor={textColor}
                  mutedTextColor={mutedTextColor}
                  onConversationSelect={handleConversationSelect}
                  getMessagePreview={getMessagePreview}
                  notificationsCount={notificationsCount}
                />
              </Box>

              {/* Vertical divider on desktop */}
              <Box
                display={{ base: 'none', md: 'block' }}
                w="1px"
                bg={borderColor}
                alignSelf="stretch"
              />

              <Box
                display={{ base: isMobileView ? 'block' : 'none', md: 'block' }}
                flex={1}
                pl={{ base: 0, md: 0 }}
              >
                <MessageDisplay
                  selectedConversation={selectedConversation}
                  messages={
                    selectedConversation?.id
                      ? conversationsMessages[selectedConversation.id] || []
                      : []
                  }
                  currentAccountId={currentAccountId}
                  borderColor={borderColor}
                  textColor={textColor}
                  mutedTextColor={mutedTextColor}
                  onBackToList={handleBackToList}
                  hasConversations={Boolean(conversations && conversations.length > 0)}
                  messagingLoading={
                    conversations === null ||
                    messagingLoading ||
                    Boolean(
                      selectedConversation?.id &&
                        conversationsMessages[selectedConversation.id] === undefined,
                    )
                  }
                />
              </Box>
            </Flex>
          </Box>
        )}
      </MyAccountLayout>
    </>
  );
};

export default Messages;
